import { FC, useEffect, useState, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom';
import styles from './app-edit-price-fields.module.scss';
import { serviceAreas } from '../../services/service-areas';
import { useTranslation } from 'react-i18next';
import Field from '../../components/shared/Field/Field';
import Modal from '../../components/shared/Modal/Modal';
import BtButton from '../bt-button/bt-button';
import { NotificationManager } from 'react-notifications';
import { Add, TrashIcon } from '../../components/svgs';
import NotesPencilIcon from '../../components/svgs/notesPencilIcon';
import Selector from '../../components/shared/Selector/Selector';
import appState from '../../state/AppStateContainer';
import RecategorizeModal from '../app-supplier-database/recategorizeModal';
import DataTable from '../../components/shared/DataTable/DataTable';
import OptionsMenu from '../../components/shared/OptionsMenu/OptionsMenu';
import SearchBarWithActions from '../../components/shared/SearchBarWithActions/SearchBarWithActions';

type Area = {
  name: string;
  _id: string;
  isActive: boolean;
  accountNumber?: string;
  slug?: string;
}

type CategoryItem = {
  label: string;
  value: string; // value is the id of the category/area
  accountNumber?: string;
  linkedCategory?: string[]; // Array of linked super admin category names
  slug?: string;
}

type LocationState = {
  addNewAreaModal?: boolean;
}

const AddAreaForm: FC<{
  categories: CategoryItem[];
  superAdminCategories: CategoryItem[];
  loading: boolean;
  isSuperAdminOrganization: boolean;
  onSave: (data: { name: string; accountNumber: string; linkedCategory: CategoryItem[] }) => Promise<void>;
  onClose: () => void;
}> = ({ categories, superAdminCategories, loading, isSuperAdminOrganization, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [linkedCategory, setLinkedCategory] = useState<CategoryItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories' });
  const [t2] = useTranslation('supplierDatabase');

  const categoryAlreadyExists = useMemo(() => {
    if (!name.trim()) return false;
    return categories.some(
      cat => cat.label.trim().toLowerCase() === name.trim().toLowerCase()
    ) 
  }, [name, categories, superAdminCategories]);

  const handleSubmit = async () => {
    if (!name.trim() || categoryAlreadyExists) return;
    setSaving(true);

    try {
      await onSave({ name: name.trim(), accountNumber: accountNumber.trim(), linkedCategory });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
      <Field
        label={t('edit.categoryNameLabel', 'Kategorinamn')}
        value={name}
        onChange={(value) => setName(value)}
        placeholder={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
        required
        errorMessage={
          categoryAlreadyExists && !saving && !saved
            ? t2('addArea.tooltip.areaAlreadyExists', 'Kategori finns redan')
            : undefined
        }
      />
      <Field
        label={t2('addArea.label.accountNumber', 'Kontonummer')}
        value={accountNumber}
        onChange={(value) => setAccountNumber(value)}
        placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
      />
      {!isSuperAdminOrganization && (
        <Selector
          isDisabled={loading}
          label={t('edit.linkToSuperAdminCategory', 'Länka till global kategori')}
          placeholder={t('edit.selectPlaceholder', 'Välj')}
          isMulti={true}
          options={superAdminCategories}
          onChange={(selected) => setLinkedCategory(selected as CategoryItem[])}
          value={linkedCategory}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
        <BtButton color="white" onClick={onClose} disabled={saving}>
          {t('edit.cancel', 'Avbryt')}
        </BtButton>
        <BtButton
          color="primary"
          onClick={handleSubmit}
          disabled={!name.trim() || categoryAlreadyExists || saving}
          loaderShow={saving}
        >
          {saving ? t2('addArea.button.saving', 'Sparar...') : t2('addArea.button.save', 'Spara yrkeskategori')}
        </BtButton>
      </div>
    </div>
  );
};

const AppEditCategories: FC<{
  modalRecategorizeShow: boolean;
  modalRecategorizeKey: number;
  addNewAreaModal: boolean;
  setAddNewAreaModal: (show: boolean) => void;
  setModalRecategorizeShow: (show: boolean) => void;
  setModalRecategorizeKey: (key: number) => void;
}> = ({ modalRecategorizeShow, modalRecategorizeKey, addNewAreaModal, setAddNewAreaModal, setModalRecategorizeShow, setModalRecategorizeKey }) => {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [superAdminCategories, setSuperAdminCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation('settings', { keyPrefix: 'editCategories'});
  const [t2] = useTranslation('supplierDatabase');
  const [linkedCategory, setLinkedCategory] = useState<CategoryItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryAccountNumber, setEditCategoryAccountNumber] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [addModalKey, setAddModalKey] = useState(0);
  const [editModalKey, setEditModalKey] = useState(0);

  useEffect(() => {
    (async () => {
      const [allAreas, allSuperAdminAreas] = await Promise.all([
        serviceAreas.getAreas(),
        serviceAreas.fetchSuperAdminAreas()
      ]);

      let mappedCategories = (allAreas ?? []).map((x: Area) => ({
        label: x.name,
        value: x._id,
        accountNumber: x.accountNumber,
        linkedCategory: [] as string[],
        slug: x.slug
      })) as CategoryItem[];


      allSuperAdminAreas?.forEach((item: any) => {
        item.customerAreas?.forEach((x: any) => {
          const categoryIndex = mappedCategories.findIndex(c => c.value === x.areaId);
          if (categoryIndex !== -1) {
            if (!mappedCategories[categoryIndex].linkedCategory) {
              mappedCategories[categoryIndex].linkedCategory = [];
            }
            mappedCategories[categoryIndex].linkedCategory!.push(item.name);
          }
        });
      });

      setCategories(mappedCategories);

      setSuperAdminCategories(allSuperAdminAreas?.map((x: Area) => ({
        label: x.name,
        value: x._id,
      })) ?? []);

      setLoading(false);
    })();

  }, []);

  const updateCategoryLinks = async (
    customerAreaId: string,
    currentLinks: string[],
    newLinks: CategoryItem[]
  ) => {
    const currentLinkIds = currentLinks
      .map(name => superAdminCategories.find(cat => cat.label === name)?.value)
      .filter(Boolean) as string[];

    const newLinkIds = newLinks.map(cat => cat.value);

    const toLink = newLinkIds.filter(id => !currentLinkIds.includes(id));
    const toUnlink = currentLinkIds.filter(id => !newLinkIds.includes(id));

    for (const superAdminId of toLink) {
      await serviceAreas.linkCategoryToSuperAdminCategory(superAdminId, customerAreaId);
    }

    for (const superAdminId of toUnlink) {
      await serviceAreas.unlinkCategoryFromSuperAdminCategory(superAdminId, customerAreaId);
    }
  };

  const handleSaveNewCategory = async (data: { name: string; accountNumber: string; linkedCategory: CategoryItem[] }) => {
    setSaving(true);
    try {
      const updatedAreas = await serviceAreas.addArea({
        name: data.name,
        accountNumber: data.accountNumber || undefined
      }) as Area[] | null;

      if (updatedAreas && Array.isArray(updatedAreas)) {
        const addedCategory = updatedAreas.find(x => x.name === data.name);

        if (addedCategory) {
          await updateCategoryLinks(addedCategory._id, [], data.linkedCategory);

          NotificationManager.success(t2('notification.success.addArea', 'Yrkeskategori tillagd'));

          setCategories(updatedAreas.map((x: Area) => ({
            label: x.name,
            value: x._id,
            accountNumber: x.accountNumber,
            linkedCategory: x._id === addedCategory._id ? data.linkedCategory.map(cat => cat.label) : []
          })));
        }

        handleCloseAddModal();
      }
    } catch (error) {
      console.error('Error adding category:', error);
      NotificationManager.error(t2('notification.error.addArea', 'Kunde inte lägga till yrkeskategori'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAddModal = () => {
    setAddNewAreaModal(false);
    setAddModalKey(prev => prev + 1);
  };

  const handleCloseEditModal = () => {
    setLinkedCategory([]);
    setEditModalShow(false);
    setEditingCategory(null);
  };

  const handleOpenEditModal = (category: CategoryItem) => {
    setEditingCategory(category);
    setEditCategoryName(category.label);
    setEditCategoryAccountNumber(category.accountNumber || '');
    setLinkedCategory(category.linkedCategory
      ? superAdminCategories.filter(cat => category.linkedCategory?.includes(cat.label))
      : []
    );
    setEditModalKey(prev => prev + 1);
    setEditModalShow(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editCategoryName.trim()) return;

    setSaving(true);
    try {
      await serviceAreas.updateAreaDetails(editingCategory.value, {
        name: editCategoryName.trim(),
        accountNumber: editCategoryAccountNumber.trim() || undefined
      });

      const currentLinks = editingCategory.linkedCategory || [];
      await updateCategoryLinks(editingCategory.value, currentLinks, linkedCategory);

      setCategories(prev => {
        const index = prev.findIndex(p => p.value === editingCategory.value);
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          label: editCategoryName.trim(),
          accountNumber: editCategoryAccountNumber.trim() || undefined,
          linkedCategory: linkedCategory.map(cat => cat.label)
        };
        return updated;
      });

      NotificationManager.success(t('notification.success.edit', 'Kategori uppdaterad'));
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating category:', error);
      NotificationManager.error(t('notification.error.edit', 'Kunde inte uppdatera kategori'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: CategoryItem) => {
    if (!window.confirm(t('delete.confirm', 'Är du säker på att du vill ta bort {{name}}?', { name: category.label }))) {
      return;
    }

    try {
      await serviceAreas.deleteArea(category.value);
      setCategories(prev => prev.filter(p => p.value !== category.value));
      NotificationManager.success(t('notification.success.delete', 'Kategori borttagen'));
    } catch (error) {
      console.error('Error deleting category:', error);
      NotificationManager.error(t('notification.error.delete', 'Kunde inte ta bort kategori'));
    }
  };

  const url = appState._getCurrentOrgUrl();
  const isSuperAdminOrganization = appState.isSuperAdmin() && appState.getCurrentOrgSlug() === 'superadmin';

  const filteredCategories = categories
    .filter((category) => {
      if (!searchTerm.trim()) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        category.label.toLowerCase().includes(searchLower) ||
        category.accountNumber?.toLowerCase().includes(searchLower) ||
        category.linkedCategory?.some(link => link.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleRowClick = (_event?: React.MouseEvent, rowData?: CategoryItem) => {
    if (isSuperAdminOrganization && rowData?.slug) {
      history.push(`${url}/categories/${rowData.slug}`);
    }
  };

  const tableColumns = [
    {
      title: t('columns.name', 'Namn'),
      field: 'label',
      render: (rowData: CategoryItem) => (
        <span style={{ fontWeight: 600, color: 'var(--gray-900)' }}>
          {rowData.label}
        </span>
      ),
      customSort: (a: CategoryItem, b: CategoryItem) => a.label.localeCompare(b.label),
    },
    {
      title: t('columns.accountNumber', 'Kontonummer'),
      field: 'accountNumber',
      render: (rowData: CategoryItem) => (
        <span style={{ color: 'var(--gray-700)' }}>{rowData.accountNumber || '–'}</span>
      ),
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order: 'asc' | 'desc') => {
        const hasA = a.accountNumber?.trim();
        const hasB = b.accountNumber?.trim();

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return parseFloat(a.accountNumber!) - parseFloat(b.accountNumber!);
      },
    },
    {
      title: t('columns.linkedCategories', 'Kopplade globala kategorier'),
      field: 'linkedCategory',
      render: (rowData: CategoryItem) => (
        <span style={{ color: 'var(--gray-700)' }}>
          {rowData.linkedCategory && rowData.linkedCategory.length > 0
            ? rowData.linkedCategory.join(', ')
            : '–'
          }
        </span>
      ),
      customSort: (a: CategoryItem, b: CategoryItem, _: any, order: 'asc' | 'desc') => {
        const lengthA = a.linkedCategory?.length ?? 0;
        const lengthB = b.linkedCategory?.length ?? 0;
        const hasA = lengthA > 0;
        const hasB = lengthB > 0;

        if (!hasA && !hasB) return 0;

        if (order === 'asc') {
          if (!hasA) return 1;
          if (!hasB) return -1;
        } else {
          if (!hasA) return -1;
          if (!hasB) return 1;
        }

        return lengthA - lengthB;
      },
      hidden: isSuperAdminOrganization,
    },
    {
      title: '',
      field: 'actions',
      sorting: false,
      width: '50px',
      render: (rowData: CategoryItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu
            options={[
              {
                label: t('menu.suppliers', 'Lägg till/ta bort leverantörer'),
                action: () => {
                  history.push(`${url}/area/${rowData.value}/suppliers`, {
                    area: { label: rowData.label, value: rowData.value }
                  });
                },
                icon: <Add size="14px" color="var(--gray-700)" />
              },
              {
                label: t('menu.edit', 'Redigera'),
                action: () => handleOpenEditModal(rowData),
                icon: <NotesPencilIcon size="14px" color="var(--gray-700)" />
              },
              {
                label: t('menu.delete', 'Ta bort'),
                action: () => handleDelete(rowData),
                icon: <TrashIcon size="14px" color="var(--gray-700)" />
              }
            ]}
          />
        </div>
      )
    }
  ];

  return <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
      <p className={styles.intro}>
        {t('intro', 'Här kan du byta namn på eller ta bort yrkeskategorier. Namnbyten påverkar både befintliga upphandlingar och leverantörer. Borttagna kategorier försvinner från leverantörer men befintliga upphandlingar påverkas inte.')}
      </p>
    </div>

    <div style={{ marginTop: '24px' }}>
      <SearchBarWithActions 
        value={searchTerm}
        onChange={setSearchTerm}
        style={{ marginBottom: '16px' }}
        className=""
        searchicon={undefined}
      >
        {undefined}
      </SearchBarWithActions>
    </div>

    <div style={{ marginTop: '24px' }} className={styles.row}>
      <DataTable
        isLoading={loading}
        columns={tableColumns}
        data={filteredCategories}
        defaultOrderBy={0}
        defaultOrderDirection="asc"
        defaultOrderByField="label"
        onRowClick={isSuperAdminOrganization ? handleRowClick : undefined}
      />
    </div>

    <Modal
      key={`addCategory${addModalKey}`}
      title={t2('addArea.placeholder.areaName', 'Lägg till yrkeskategori')}
      show={addNewAreaModal}
      setShow={handleCloseAddModal}
      hasCloseButton
    >
      <AddAreaForm
        categories={categories}
        superAdminCategories={superAdminCategories}
        loading={loading}
        isSuperAdminOrganization={isSuperAdminOrganization}
        onSave={handleSaveNewCategory}
        onClose={handleCloseAddModal}
      />
    </Modal>

    <Modal
      key={`editCategory${editModalKey}`}
      title={t('edit.modalTitle', 'Redigera kategori')}
      show={editModalShow}
      setShow={handleCloseEditModal}
      hasCloseButton
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '8px 0' }}>
        <Field
          label={t('edit.categoryNameLabel', 'Kategorinamn')}
          value={editCategoryName}
          onChange={(value) => setEditCategoryName(value)}
          required
        />
        <Field
          label={t('edit.accountNumberLabel', 'Kontonummer')}
          value={editCategoryAccountNumber}
          onChange={(value) => setEditCategoryAccountNumber(value)}
          placeholder={t('edit.accountNumberPlaceholder', 'T.ex. 1234')}
        />
        {!isSuperAdminOrganization && <Selector
          label={t('edit.linkedCategoryLabel', 'Kopplat till global kategori')}
          options={superAdminCategories}
          isMulti={true}
          onChange={(value) => {
            setLinkedCategory(value as CategoryItem[]);
          }}
          value={linkedCategory}
        />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
          <BtButton
            color="white"
            onClick={handleCloseEditModal}
            disabled={saving}
          >
            {t('edit.cancel', 'Avbryt')}
          </BtButton>

          <BtButton
            color="primary"
            onClick={handleSaveEdit}
            disabled={!editCategoryName.trim() || saving}
            loaderShow={saving}
          >
            {saving ? t('edit.saving', 'Sparar...') : t('edit.save', 'Spara')}
          </BtButton>
        </div>
      </div>
    </Modal>

    <RecategorizeModal
      show={modalRecategorizeShow}
      setShow={setModalRecategorizeShow}
      key={modalRecategorizeKey}
      areas={categories}
      onSave={() => { }}
    />
  </div>;
}

export default AppEditCategories;
